import React, { useEffect, useCallback } from 'react'
import styled from 'styled-components'
import LocationsTable from './components/LocationsTable'
import { ListSectorsIntranet, CreateSector } from '../../API/core'
import { Col, Modal, ModalBody, ModalFooter, ModalHeader, Row, Spinner } from 'reactstrap'
import { DebounceInput } from 'react-debounce-input'
import { SearchableCountries, SearchableProvinces, SearchableCities } from '../../Components/FormGroups'
import { OurToaster } from '../../Components/elements/Toast'

function Locations(props) {
	const [loading, setLoading] = React.useState(false)
	const [locations, setLocations] = React.useState([])
	const [search, setSearch] = React.useState('')
	const [nextParams, setNextParams] = React.useState('')
	const [modal, toggle] = React.useState(false)
	const [newLocation, setNewLocation] = React.useState({
		country: null,
		province: null,
		city: null,
		name: null,
	})

	// Filter state
	const [filters, setFilters] = React.useState({
		country: null,
		province: null,
		city: null,
	})

	// Keys to force re-render of child selects when parent changes
	const [selectKeys, setSelectKeys] = React.useState({
		country: 0,
		province: 0,
		city: 0,
	})

	const handleNewLocation = (value, index) => {
		let newLocation_ = Object.assign({}, newLocation)
		newLocation_[index] = value
		setNewLocation(newLocation_)
	}

	/**
	 * Builds query string from current filters
	 * @returns {string} Query string for API request
	 */
	const buildFilterParams = useCallback(() => {
		const params = []
		if (search) params.push(`name=${search}`)
		if (filters.country) params.push(`country=${filters.country}`)
		if (filters.province) params.push(`province=${filters.province}`)
		if (filters.city) params.push(`city=${filters.city}`)

		return params.join('&')
	}, [search, filters])

	const initData = async (params, clear = false) => {
		let response = await ListSectorsIntranet(`?${params}`)
		let locations_ = clear ? [] : Array.from(locations)
		const result = locations_.concat(response.data.results)
		setLocations(result)

		if (response.data.next !== null) {
			setNextParams(response.data.next.split('?')[1])
		} else {
			setNextParams(null)
		}
	}

	/**
	 * Handles filter change with cascading reset logic
	 * @param {string} field - The filter field being changed
	 * @param {any} value - The new value for the filter
	 */
	const handleFilterChange = (field, value) => {
		let newFilters = { ...filters }
		newFilters[field] = value

		// Reset dependent filters when parent changes
		if (field === 'country') {
			newFilters.province = null
			newFilters.city = null
			setSelectKeys((prev) => ({
				...prev,
				province: prev.province + 1,
				city: prev.city + 1,
			}))
		} else if (field === 'province') {
			newFilters.city = null
			setSelectKeys((prev) => ({
				...prev,
				city: prev.city + 1,
			}))
		}

		setFilters(newFilters)
	}

	/**
	 * Clears all filters and reloads data
	 */
	const clearFilters = () => {
		setFilters({
			country: null,
			province: null,
			city: null,
		})
		setSearch('')
		setSelectKeys((prev) => ({
			country: prev.country + 1,
			province: prev.province + 1,
			city: prev.city + 1,
		}))
	}

	const handleSearch = (e) => {
		setSearch(e.target.value)
	}

	const handleCreateSector = async () => {
		setLoading(true)
		try {
			await CreateSector(newLocation)
			OurToaster.show({ message: 'Sector creado satisfactoriamente.', intent: 'success' })
			toggle(false)
			// Reload data after creating sector
			initData(buildFilterParams(), true)
		} catch (err) {
			OurToaster.show({ message: 'Ha ocurrido un error.', intent: 'danger' })
		}
		setLoading(false)
	}

	// Effect to reload data when filters or search change
	useEffect(() => {
		initData(buildFilterParams(), true)
	}, [filters, search])

	return (
		<Container className="px-3">
			<div className="card my-3 ">
				<div className="card-body">
					<Row className="mb-3">
						<Col xs="12" sm="6" md="4" lg="3" xl="2">
							<DebounceInput
								placeholder="Buscar por nombre..."
								minLength={3}
								debounceTimeout={500}
								className="form-control"
								value={search}
								onChange={handleSearch}
							/>
						</Col>
						<Col className="text-right">
							<button onClick={() => toggle(true)} className="btn btn-success">
								Crear sector
							</button>
						</Col>
					</Row>

					{/* Location Filters Row */}
					<Row className="mb-3 align-items-end">
						<Col xs="12" sm="6" md="3" lg="2">
							<SearchableCountries
								key={`country-${selectKeys.country}`}
								label="País"
								placeholder="Seleccionar país"
								isClearable
								onChange={(value) => handleFilterChange('country', value)}
								sm
							/>
						</Col>
						<Col xs="12" sm="6" md="3" lg="2">
							<SearchableProvinces
								key={`province-${selectKeys.province}`}
								label="Provincia"
								placeholder="Seleccionar provincia"
								isClearable
								disabled={!filters.country}
								params={filters.country ? `&country=${filters.country}` : ''}
								onChange={(value) => handleFilterChange('province', value)}
								sm
							/>
						</Col>
						<Col xs="12" sm="6" md="3" lg="2">
							<SearchableCities
								key={`city-${selectKeys.city}`}
								label="Ciudad"
								placeholder="Seleccionar ciudad"
								isClearable
								disabled={!filters.province}
								params={filters.province ? `province=${filters.province}` : ''}
								onChange={(value) => handleFilterChange('city', value)}
								sm
							/>
						</Col>
						<Col xs="12" sm="6" md="auto" className="mt-2 mt-md-0">
							<button
								className="btn btn-outline-secondary btn-sm"
								onClick={clearFilters}
								disabled={!filters.country && !filters.province && !filters.city && !search}>
								Limpiar filtros
							</button>
						</Col>
					</Row>

					<Row>
						<Col>
							<LocationsTable locations={locations} RetrieveData={initData} nextParams={nextParams} />
						</Col>
					</Row>
				</div>
			</div>

			<Modal isOpen={modal} toggle={() => toggle(false)}>
				<ModalHeader>Crear nuevo sector</ModalHeader>
				<ModalBody>
					<label className="w-100">
						País
						<SearchableCountries onChange={(value) => handleNewLocation(value, 'country')} />
					</label>
					<label className="w-100">
						Provincia
						<SearchableProvinces
							onChange={(value) => handleNewLocation(value, 'province')}
							disabled={newLocation.country === null || newLocation.country === ''}
							params={`country=${newLocation.country}`}
						/>
					</label>
					<label className="w-100">
						Ciudad
						<SearchableCities
							onChange={(value) => handleNewLocation(value, 'city')}
							disabled={newLocation.province === null || newLocation.province === ''}
							params={`province=${newLocation.province}`}
						/>
					</label>
					<label className="w-100">
						Nuevo sector
						<input
							className="form-control"
							onChange={(e) => handleNewLocation(e.target.value, 'name')}
							disabled={newLocation.city === null || newLocation.city === ''}
						/>
					</label>
				</ModalBody>
				<ModalFooter>
					<button className="btn btn-secondary" onClick={() => toggle(false)} disabled={loading}>
						Cancelar
					</button>
					<button className="btn btn-primary" onClick={handleCreateSector} disabled={loading}>
						{loading ? <Spinner /> : 'Guardar'}{' '}
					</button>
				</ModalFooter>
			</Modal>
		</Container>
	)
}
const Container = styled.div``
export default Locations
